import { useMemo, useCallback } from "react"
import { IRoute, IStep } from "interfaces"
import { ImdfFeature } from "providers/venue/types"
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom"
import { get, isNil } from "lodash"

import { getFeatureDetail } from "utils/feature"
import { useVenue } from "providers/venue"
import { Q_AUTOPLAY } from "constant"
interface OutletContext {
  route: IRoute
  steps: IStep[]
  origin?: ImdfFeature
  destination?: ImdfFeature
  elevatorPreferredMode?: boolean
  duration: number
  distance: number
  scannedLocations: ImdfFeature[]
  userLocation: ImdfFeature
  isCalculatingRoute: boolean
  toggleElevatorPreference: () => void
}

export const useDirectionOverview = () => {
  const navigate = useNavigate()
  const {
    stepAutoplayConfig,
    mapTheme,
    defaultRoutePath: exitPath,
    getRelatedLevelWithSteps,
  } = useVenue()
  const [searchParams] = useSearchParams()
  const {
    steps = [],
    origin,
    destination,
    duration,
    distance,
    route,
    scannedLocations,
    userLocation,
    isCalculatingRoute,
    elevatorPreferredMode = false,
    toggleElevatorPreference,
  } = useOutletContext<OutletContext>()

  const {
    step = "1",
    origin: originParam,
    destination: destinationParam,
  } = useParams()

  // Steps for direction overview.
  const overviewSteps = useMemo(
    // Remove arrival step to avoid redundancy with predefined destination step in UI.
    () => steps.slice(0, steps.length - 1),
    [steps]
  )

  const isReady = useMemo(() => steps.length > 0, [steps])

  const currentStepNumber = Number(step)

  const originData = useMemo(() => getFeatureDetail(origin), [origin])
  const originVenueData = useMemo(
    () => getFeatureDetail(get(origin, "properties.venue")) || {},
    [origin]
  )

  const originName = useMemo(() => get(originData, "name"), [originData])
  const originVenueName = useMemo(
    () => get(originVenueData, "name"),
    [originVenueData]
  )
  const originLevelName = useMemo(() => get(originData, "level"), [originData])

  const originLogo = useMemo(() => get(originData, "logo"), [originData])

  const originVenueLogo = useMemo(
    () => get(originData, "venueLogo"),
    [originData]
  )

  const originCategoryIconPath = useMemo(() => {
    const category = get(originData, "category", "")
    const categoryName =
      typeof category === "string" ? category : get(category, "en")
    const categoryIconPath = get(
      mapTheme,
      `${categoryName}.label.marker.symbol.markerPath`
    )

    const iconPath = get(originData, "categoryIconPath", categoryIconPath)
    const image = get(originData, "categoryIconImage")

    if (!iconPath && !image) return null
    return {
      icon_path: iconPath,
      image,
    }
  }, [originData, mapTheme])

  const destinationData = useMemo(
    () => getFeatureDetail(destination),
    [destination]
  )
  const destinationVenueData = useMemo(
    () => getFeatureDetail(get(destination, "properties.venue")) || {},
    [destination]
  )

  const destinationName = useMemo(
    () => get(destinationData, "name"),
    [destinationData]
  )
  const destinationVenueName = useMemo(
    () => get(destinationVenueData, "name"),
    [destinationVenueData]
  )
  const destinationLevelName = useMemo(
    () => get(destinationData, "level"),
    [destinationData]
  )

  const destinationLogo = useMemo(
    () => get(destinationData, "logo"),
    [destinationData]
  )

  const destinationVenueLogo = useMemo(
    () => get(destinationData, "venueLogo"),
    [destinationData]
  )

  const destinationCategoryIconPath = useMemo(() => {
    const category = get(destinationData, "category", "")
    const categoryName =
      typeof category === "string" ? category : get(category, "en")
    const categoryIconPath = get(
      mapTheme,
      `${categoryName}.label.marker.symbol.markerPath`
    )

    const iconPath = get(destinationData, "categoryIconPath", categoryIconPath)
    const image = get(destinationData, "categoryIconImage")

    if (!iconPath && !image) return null

    return {
      icon_path: iconPath,
      image,
    }
  }, [destinationData, mapTheme])

  const currentOrdinal = Number(
    searchParams.get("ordinal") || origin?.properties?.ordinal
  )

  const relatedLevelWithSteps = useMemo(
    () => getRelatedLevelWithSteps(steps),
    [getRelatedLevelWithSteps, steps]
  )

  const progressValue = (currentStepNumber * 100) / steps.length

  const isGoingToOtherVenue = useMemo(() => {
    const originVenueId = get(originVenueData, "id")
    const destinationVenueId = get(destinationVenueData, "id")
    return (
      !isNil(originVenueId) &&
      !isNil(originVenueId) &&
      originVenueId !== destinationVenueId
    )
  }, [originVenueData, destinationVenueData])

  const startNavigate = useCallback(() => {
    if (!isNil(originParam) && !isNil(destinationParam)) {
      searchParams.set(Q_AUTOPLAY, stepAutoplayConfig.enable)
      navigate(
        `/maps/dir/${originParam}/${destinationParam}/navigate/steps/1?${searchParams.toString()}`,
        { replace: true }
      )
    }
  }, [
    navigate,
    originParam,
    destinationParam,
    searchParams,
    stepAutoplayConfig,
  ])

  const changeOrdinal = useCallback(
    (ordinal: number) => {
      searchParams.set("ordinal", String(ordinal))
      navigate(`?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  return {
    isCalculatingRoute,
    overviewSteps,
    steps,
    duration,
    distance,
    scannedLocations,
    userLocation,
    isReady,
    progressValue,
    originParam,
    destinationParam,
    currentStepNumber,
    currentOrdinal,
    originData,
    originLevelName,
    originName,
    originVenueName,
    originLogo,
    originVenueLogo,
    originCategoryIconPath,
    originVenueData,
    destinationData,
    destinationName,
    destinationLevelName,
    destinationVenueName,
    destinationLogo,
    destinationVenueLogo,
    destinationCategoryIconPath,
    destinationVenueData,
    isGoingToOtherVenue,
    relatedLevelWithSteps,
    route,
    elevatorPreferredMode,
    exitPath,
    toggleElevatorPreference,
    startNavigate,
    changeOrdinal,
  }
}
